import Layout from "@/components/layout/Layout";
import { Target, Heart, Award, Users, Globe, Lightbulb } from "lucide-react";

const AboutPage = () => {
  const stats = [
    { number: "50,000+", label: "Students Worldwide" },
    { number: "200+", label: "Expert Courses" },
    { number: "95%", label: "Success Rate" },
    { number: "50+", label: "Industry Partners" },
  ];

  const values = [
    {
      icon: Target,
      title: "Excellence",
      description: "We strive for excellence in everything we do, from course content to student support.",
    },
    {
      icon: Heart,
      title: "Empathy",
      description: "We understand each student's unique journey and provide personalized guidance.",
    },
    {
      icon: Lightbulb,
      title: "Innovation",
      description: "We constantly evolve our methods to stay ahead of industry trends.",
    },
    {
      icon: Users,
      title: "Community",
      description: "We foster a supportive community where learners help each other grow.",
    },
  ];

  const team = [
    {
      name: "Dr. Elizabeth Chen",
      role: "Founder & CEO",
      image: "https://images.pexels.com/photos/3756679/pexels-photo-3756679.jpeg?auto=compress&cs=tinysrgb&w=300",
      bio: "Former Stanford professor with 20 years in education technology.",
    },
    {
      name: "Michael Roberts",
      role: "Chief Learning Officer",
      image: "https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=300",
      bio: "Educational psychologist specializing in adult learning and skill development.",
    },
    {
      name: "Sarah Thompson",
      role: "Head of Career Services",
      image: "https://images.pexels.com/photos/3796217/pexels-photo-3796217.jpeg?auto=compress&cs=tinysrgb&w=300",
      bio: "Former HR Director at Fortune 500 companies with expertise in talent development.",
    },
    {
      name: "David Park",
      role: "Chief Technology Officer",
      image: "https://images.pexels.com/photos/1222271/pexels-photo-1222271.jpeg?auto=compress&cs=tinysrgb&w=300",
      bio: "Tech leader with experience at Google and Meta, passionate about EdTech.",
    },
  ];

  return (
    <Layout>
      {/* Hero */}
      <section className="bg-slate-50 py-20" data-testid="about-hero">
        <div className="container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <span className="overline">About Us</span>
              <h1 className="heading-1">Empowering World-Class Careers</h1>
              <p className="body-text text-lg">
                WCareers was founded with a simple mission: to help individuals achieve their 
                professional potential through world-class education and personalized career guidance.
              </p>
              <p className="body-text">
                Since our founding in 2018, we've helped over 50,000 students transform their careers 
                through expert-led courses, one-on-one counseling, and AI-powered guidance.
              </p>
            </div>
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/4884122/pexels-photo-4884122.jpeg?auto=compress&cs=tinysrgb&w=600"
                alt="Modern office"
                className="rounded-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-slate-900" data-testid="about-stats">
        <div className="container-padding">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="font-heading text-4xl lg:text-5xl font-light text-white">{stat.number}</p>
                <p className="text-slate-400 mt-2">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="section-padding bg-white" data-testid="mission-section">
        <div className="container-padding">
          <div className="max-w-4xl mx-auto text-center">
            <span className="overline">Our Mission</span>
            <h2 className="heading-2 mt-4 mb-8">
              Making World-Class Career Education Accessible to Everyone
            </h2>
            <p className="body-text text-lg">
              We believe that everyone deserves access to quality education and career guidance, 
              regardless of their background or location. Our platform combines cutting-edge technology 
              with human expertise to deliver personalized learning experiences that drive real career outcomes.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding bg-slate-50" data-testid="values-section">
        <div className="container-padding">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="overline">Our Values</span>
            <h2 className="heading-2 mt-4">What Drives Us</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-white p-8 border border-slate-100 hover:shadow-xl transition-all duration-300"
                data-testid={`value-card-${index}`}
              >
                <div className="w-14 h-14 bg-lime-50 rounded-xl flex items-center justify-center mb-6">
                  <value.icon className="w-7 h-7 text-lime-600" />
                </div>
                <h3 className="font-heading text-xl font-medium text-slate-900 mb-3">{value.title}</h3>
                <p className="text-slate-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="section-padding bg-white" data-testid="team-section">
        <div className="container-padding">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="overline">Our Team</span>
            <h2 className="heading-2 mt-4">Meet the Leadership</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.map((member, index) => (
              <div
                key={index}
                className="text-center group"
                data-testid={`team-card-${index}`}
              >
                <div className="relative mb-6 overflow-hidden rounded-2xl">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <h3 className="font-heading text-xl font-medium text-slate-900">{member.name}</h3>
                <p className="text-lime-600 font-medium mb-2">{member.role}</p>
                <p className="text-slate-600 text-sm">{member.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners */}
      <section className="section-padding bg-slate-50" data-testid="partners-section">
        <div className="container-padding">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="overline">Our Partners</span>
            <h2 className="heading-2 mt-4">Trusted by Leading Companies</h2>
          </div>
          <div className="flex flex-wrap justify-center items-center gap-12">
            <div className="text-3xl font-bold text-slate-300">Google</div>
            <div className="text-3xl font-bold text-slate-300">Microsoft</div>
            <div className="text-3xl font-bold text-slate-300">Amazon</div>
            <div className="text-3xl font-bold text-slate-300">Meta</div>
            <div className="text-3xl font-bold text-slate-300">Apple</div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default AboutPage;
